import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  createPost,
  duplicatePost,
  getPost,
  listPosts,
  setPostApproval,
  updatePost,
  type ApprovalStatus,
  type CreatePostInput,
  type PostFilters,
  type SocialPlatform,
  type SocialPost,
  type UpdatePostInput,
} from '../lib/socialPostsApi';

export const SOCIAL_POSTS_KEY = ['social-posts'] as const;

export function useSocialPosts(filters: PostFilters, enabled = true) {
  return useQuery({
    queryKey: [...SOCIAL_POSTS_KEY, 'list', filters],
    queryFn: () => listPosts(filters),
    enabled,
    placeholderData: (prev) => prev,
  });
}

export function useSocialPost(taskId: string | undefined) {
  return useQuery({
    queryKey: [...SOCIAL_POSTS_KEY, 'detail', taskId],
    queryFn: () => getPost(taskId!),
    enabled: Boolean(taskId),
  });
}

/** Posts are tasks too: every write refreshes both caches. */
function useInvalidate() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: SOCIAL_POSTS_KEY });
    void qc.invalidateQueries({ queryKey: ['tasks'] });
    void qc.invalidateQueries({ queryKey: ['task'] });
  };
}

export function useCreatePost() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: CreatePostInput) => createPost(input),
    onSuccess: invalidate,
  });
}

export function useUpdatePost(taskId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (input: UpdatePostInput) => updatePost(taskId, input),
    onSuccess: invalidate,
  });
}

type PostList = { posts: SocialPost[]; capped: boolean };

/**
 * Drag on the calendar: any post can move, so the id comes with the call. The
 * list is patched right away so the card stays where it was dropped, and put
 * back if the server refuses.
 */
export function useMovePost() {
  const qc = useQueryClient();
  const invalidate = useInvalidate();
  const lists = { queryKey: [...SOCIAL_POSTS_KEY, 'list'] };
  return useMutation({
    mutationFn: (v: { id: string; publishAt: string }) =>
      updatePost(v.id, { publishAt: v.publishAt }),
    onMutate: async ({ id, publishAt }) => {
      await qc.cancelQueries(lists);
      const previous = qc.getQueriesData<PostList>(lists);
      for (const [key, data] of previous) {
        if (!data) continue;
        qc.setQueryData<PostList>(key, {
          ...data,
          posts: data.posts.map((p) => (p.id === id ? { ...p, publishAt } : p)),
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      ctx?.previous.forEach(([key, data]) => qc.setQueryData(key, data));
    },
    onSettled: invalidate,
  });
}

export function useSetPostApproval(taskId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { status: ApprovalStatus; comment?: string }) =>
      setPostApproval(taskId, v.status, v.comment),
    onSuccess: invalidate,
  });
}

export function useDuplicatePost(taskId: string) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: (v: { platform: SocialPlatform; publishAt?: string }) =>
      duplicatePost(taskId, v.platform, v.publishAt),
    onSuccess: invalidate,
  });
}
